import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import VehicleAssignmentPanel from './VehicleAssignmentPanel';
import LeaseContractGenerator from './LeaseContractGenerator';

const STATUS_STYLES = {
  active: { bg: 'rgba(52,211,153,0.12)', text: '#34d399', border: 'rgba(52,211,153,0.3)', label: 'Active' },
  suspended: { bg: 'rgba(239,68,68,0.12)', text: '#ef4444', border: 'rgba(239,68,68,0.3)', label: 'Suspended' },
  pending: { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)', label: 'Pending' },
};

const LEASE_STATUS_STYLES = {
  active: { bg: 'rgba(52,211,153,0.10)', text: '#34d399', label: 'Active' },
  draft: { bg: 'rgba(148,163,184,0.10)', text: '#94a3b8', label: 'Draft' },
  sent: { bg: 'rgba(96,165,250,0.10)', text: '#60a5fa', label: 'Sent' },
  accepted: { bg: 'rgba(167,139,250,0.10)', text: '#a78bfa', label: 'Accepted' },
  ended: { bg: 'rgba(239,68,68,0.10)', text: '#ef4444', label: 'Ended' },
};

// ── Edit Modal ────────────────────────────────────────────────────────────────
const EditAccountModal = ({ account, onClose, onSaved }) => {
  const [form, setForm] = useState({
    company_name: account?.company_name || '',
    billing_email: account?.billing_email || '',
    contact_person: account?.contact_person || '',
    contact_phone: account?.contact_phone || '',
    reg_no: account?.reg_no || '',
    address: account?.address || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: updateErr } = await supabase
        ?.from('corporate_accounts')
        ?.update({
          company_name: form?.company_name,
          billing_email: form?.billing_email,
          contact_person: form?.contact_person || null,
          contact_phone: form?.contact_phone || null,
          reg_no: form?.reg_no || null,
          address: form?.address || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', account?.id);
      if (updateErr) throw updateErr;
      onSaved();
    } catch (err) {
      setError(err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="bg-surface rounded-2xl border border-border w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-text-primary font-bold text-base flex items-center gap-2">
            <Icon name="Edit3" size={18} className="text-[#ffd700]" />
            Edit Corporate Account
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors">
            <Icon name="X" size={18} className="text-text-secondary" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-text-secondary mb-1">Company Name *</label>
              <input
                value={form?.company_name}
                onChange={e => handleChange('company_name', e?.target?.value)}
                className="input-field w-full text-sm"
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Billing Email *</label>
              <input
                type="email"
                value={form?.billing_email}
                onChange={e => handleChange('billing_email', e?.target?.value)}
                className="input-field w-full text-sm"
                placeholder="billing@company.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Registration No.</label>
              <input
                value={form?.reg_no}
                onChange={e => handleChange('reg_no', e?.target?.value)}
                className="input-field w-full text-sm"
                placeholder="e.g. CPR/2024/001"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Contact Person</label>
              <input
                value={form?.contact_person}
                onChange={e => handleChange('contact_person', e?.target?.value)}
                className="input-field w-full text-sm"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Contact Phone</label>
              <input
                value={form?.contact_phone}
                onChange={e => handleChange('contact_phone', e?.target?.value)}
                className="input-field w-full text-sm"
                placeholder="+254 7XX XXX XXX"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-text-secondary mb-1">Address</label>
              <input
                value={form?.address}
                onChange={e => handleChange('address', e?.target?.value)}
                className="input-field w-full text-sm"
                placeholder="Physical address"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="btn-secondary text-sm px-4 py-2">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form?.company_name || !form?.billing_email}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Panel ────────────────────────────────────────────────────────────────
const CorporateAccountsPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [editAccount, setEditAccount] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTab, setExpandedTab] = useState({}); // { [accountId]: 'leases' | 'vehicles' }
  const [contractAccount, setContractAccount] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        ?.from('corporate_accounts')
        ?.select(`
          *,
          corporate_leases(id, lease_status, lease_number, start_at, end_at, monthly_price, vehicles),
          corporate_lease_requests!source_request_id(id, fleet_size, lease_term, company_name)
        `)
        ?.order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('corp_status', filterStatus);
      }

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      setAccounts(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load corporate accounts');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const updateAccountStatus = async (accountId, newStatus) => {
    setActionLoading(accountId + newStatus);
    try {
      const { error: updateErr } = await supabase
        ?.from('corporate_accounts')
        ?.update({ corp_status: newStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', accountId);
      if (updateErr) throw updateErr;
      showToast(`Account ${newStatus === 'suspended' ? 'suspended' : 'cancelled'} successfully`);
      setConfirmAction(null);
      fetchAccounts();
    } catch (err) {
      showToast(err?.message || 'Action failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (val) => {
    if (!val) return '—';
    return `KES ${Number(val)?.toLocaleString('en-KE', { minimumFractionDigits: 0 })}`;
  };

  // Compute fleet vehicle count from leases
  const getFleetCount = (account) => {
    const leases = account?.corporate_leases || [];
    let total = 0;
    leases?.forEach(lease => {
      const vehicles = Array.isArray(lease?.vehicles) ? lease?.vehicles : [];
      total += vehicles?.length;
    });
    // Fallback: use fleet_size from the source request
    if (total === 0 && account?.corporate_lease_requests?.fleet_size) {
      return account?.corporate_lease_requests?.fleet_size;
    }
    return total;
  };

  const getActiveLeases = (account) => {
    return (account?.corporate_leases || [])?.filter(l => l?.lease_status === 'active');
  };

  const filteredAccounts = accounts?.filter(acc => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return (
      acc?.company_name?.toLowerCase()?.includes(q) ||
      acc?.contact_person?.toLowerCase()?.includes(q) ||
      acc?.billing_email?.toLowerCase()?.includes(q)
    );
  });

  const statusCounts = accounts?.reduce((acc, a) => {
    acc[a?.corp_status] = (acc?.[a?.corp_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-surface rounded-xl border border-border p-5 space-y-5">
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-20 right-5 z-[300] px-4 py-3 rounded-xl text-sm font-medium shadow-xl flex items-center gap-2 animate-fade-in"
          style={{
            background: toast?.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(52,211,153,0.15)',
            border: `1px solid ${toast?.type === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(52,211,153,0.4)'}`,
            color: toast?.type === 'error' ? '#ef4444' : '#34d399',
          }}
        >
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={16} />
          {toast?.msg}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-text-primary font-bold text-lg flex items-center gap-2">
            <Icon name="Building2" size={20} className="text-[#ffd700]" />
            Corporate Accounts
          </h2>
          <p className="text-text-secondary text-sm mt-0.5">Approved business accounts — leases, fleet, and account management</p>
        </div>
        <button
          onClick={fetchAccounts}
          className="p-2 rounded-lg border border-border hover:bg-surface-hover transition-colors"
          title="Refresh"
        >
          <Icon name="RefreshCw" size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Accounts', value: accounts?.length, icon: 'Building2', color: '#ffd700' },
          { label: 'Active', value: statusCounts?.active || 0, icon: 'CheckCircle2', color: '#34d399' },
          { label: 'Suspended', value: statusCounts?.suspended || 0, icon: 'PauseCircle', color: '#ef4444' },
          {
            label: 'Active Leases',
            value: accounts?.reduce((sum, a) => sum + getActiveLeases(a)?.length, 0),
            icon: 'FileText',
            color: '#60a5fa',
          },
        ]?.map(stat => (
          <div
            key={stat?.label}
            className="rounded-xl p-3 flex items-center gap-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${stat?.color}18` }}>
              <Icon name={stat?.icon} size={17} style={{ color: stat?.color }} />
            </div>
            <div>
              <p className="text-text-primary font-bold text-lg leading-none">{stat?.value}</p>
              <p className="text-text-secondary text-xs mt-0.5">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            placeholder="Search by company, contact, or email…"
            className="input-field w-full text-sm pl-9"
          />
        </div>
        {/* Status Tabs */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'active', 'suspended', 'pending']?.map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize"
              style={{
                background: filterStatus === s ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)',
                color: filterStatus === s ? '#ffd700' : 'var(--color-text-secondary)',
                border: `1px solid ${filterStatus === s ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              {s === 'all' ? 'All' : STATUS_STYLES?.[s]?.label || s}
              {s !== 'all' && statusCounts?.[s] ? (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  {statusCounts?.[s]}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
          {error}
        </div>
      )}
      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-border animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
      )}
      {/* Empty */}
      {!loading && !error && filteredAccounts?.length === 0 && (
        <div className="text-center py-14">
          <Icon name="Building2" size={42} className="text-text-secondary mx-auto mb-3 opacity-30" />
          <p className="text-text-secondary text-sm">
            {searchQuery ? 'No accounts match your search' : `No ${filterStatus !== 'all' ? filterStatus : ''} corporate accounts found`}
          </p>
        </div>
      )}
      {/* Account Cards */}
      {!loading && filteredAccounts?.map(account => {
        const statusStyle = STATUS_STYLES?.[account?.corp_status] || STATUS_STYLES?.pending;
        const isExpanded = expandedId === account?.id;
        const activeLeases = getActiveLeases(account);
        const fleetCount = getFleetCount(account);
        const allLeases = account?.corporate_leases || [];
        const currentTab = expandedTab?.[account?.id] || 'leases';

        return (
          <div
            key={account?.id}
            className="rounded-xl border transition-all"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderColor: isExpanded ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.08)',
            }}
          >
            {/* Card Header */}
            <div
              className="p-4 cursor-pointer flex items-start justify-between gap-3"
              onClick={() => setExpandedId(isExpanded ? null : account?.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-text-primary font-semibold text-sm">
                    {account?.company_name || 'Unnamed Company'}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: statusStyle?.bg, color: statusStyle?.text, border: `1px solid ${statusStyle?.border}` }}
                  >
                    {statusStyle?.label}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  {account?.contact_person && (
                    <span className="text-text-secondary text-xs flex items-center gap-1">
                      <Icon name="User" size={11} /> {account?.contact_person}
                    </span>
                  )}
                  {account?.billing_email && (
                    <span className="text-text-secondary text-xs flex items-center gap-1">
                      <Icon name="Mail" size={11} /> {account?.billing_email}
                    </span>
                  )}
                  {account?.contact_phone && (
                    <span className="text-text-secondary text-xs flex items-center gap-1">
                      <Icon name="Phone" size={11} /> {account?.contact_phone}
                    </span>
                  )}
                </div>
                {/* Quick Stats Row */}
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <span className="text-xs flex items-center gap-1.5" style={{ color: '#60a5fa' }}>
                    <Icon name="FileText" size={12} />
                    <strong>{activeLeases?.length}</strong> active lease{activeLeases?.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs flex items-center gap-1.5" style={{ color: '#a78bfa' }}>
                    <Icon name="Car" size={12} />
                    <strong>{fleetCount}</strong> fleet vehicle{fleetCount !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs text-text-secondary flex items-center gap-1">
                    <Icon name="Calendar" size={11} /> Since {formatDate(account?.created_at)}
                  </span>
                </div>
              </div>

              {/* Action Buttons (always visible) */}
              <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e?.stopPropagation()}>
                {/* Edit */}
                <button
                  onClick={() => setEditAccount(account)}
                  className="p-2 rounded-lg border border-border hover:bg-surface-hover transition-colors"
                  title="Edit account"
                >
                  <Icon name="Edit3" size={14} className="text-text-secondary" />
                </button>
                {/* Generate Contract PDF */}
                <button
                  onClick={() => setContractAccount(account)}
                  className="p-2 rounded-lg border transition-colors"
                  style={{ borderColor: 'rgba(255,215,0,0.3)', color: '#ffd700' }}
                  title="Generate lease contract PDF"
                >
                  <Icon name="Download" size={14} />
                </button>
                {/* Suspend / Reactivate */}
                {account?.corp_status !== 'suspended' ? (
                  <button
                    onClick={() => setConfirmAction({ account, type: 'suspend' })}
                    disabled={actionLoading === account?.id + 'suspended'}
                    className="p-2 rounded-lg border transition-colors disabled:opacity-50"
                    style={{ borderColor: 'rgba(251,191,36,0.3)', color: '#fbbf24' }}
                    title="Suspend account"
                  >
                    <Icon name="PauseCircle" size={14} />
                  </button>
                ) : (
                  <button
                    onClick={() => updateAccountStatus(account?.id, 'active')}
                    disabled={!!actionLoading}
                    className="p-2 rounded-lg border transition-colors disabled:opacity-50"
                    style={{ borderColor: 'rgba(52,211,153,0.3)', color: '#34d399' }}
                    title="Reactivate account"
                  >
                    <Icon name="PlayCircle" size={14} />
                  </button>
                )}
                {/* Cancel */}
                <button
                  onClick={() => setConfirmAction({ account, type: 'cancel' })}
                  disabled={!!actionLoading}
                  className="p-2 rounded-lg border transition-colors disabled:opacity-50"
                  style={{ borderColor: 'rgba(239,68,68,0.3)', color: '#ef4444' }}
                  title="Cancel / deactivate account"
                >
                  <Icon name="XCircle" size={14} />
                </button>
                {/* Expand toggle */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : account?.id)}
                  className="p-2 rounded-lg border border-border hover:bg-surface-hover transition-colors"
                >
                  <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary" />
                </button>
              </div>
            </div>
            {/* Expanded Detail */}
            {isExpanded && (
              <div className="border-t border-border px-4 pb-4 pt-3 space-y-4 animate-fade-in">
                {/* Account Details */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Registration No.', value: account?.reg_no || '—', icon: 'Hash' },
                    { label: 'Address', value: account?.address || '—', icon: 'MapPin' },
                    { label: 'Approved At', value: formatDate(account?.approved_at), icon: 'CheckCircle2' },
                  ]?.map(detail => (
                    <div key={detail?.label} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-text-secondary text-xs flex items-center gap-1 mb-1">
                        <Icon name={detail?.icon} size={11} /> {detail?.label}
                      </p>
                      <p className="text-text-primary text-sm font-medium truncate">{detail?.value}</p>
                    </div>
                  ))}
                </div>

                {/* Tab Switcher */}
                <div className="flex gap-2">
                  {[
                    { key: 'leases', label: 'Leases', icon: 'FileText', count: allLeases?.length, color: '#60a5fa' },
                    { key: 'vehicles', label: 'Vehicle Assignments', icon: 'Truck', count: null, color: '#a78bfa' },
                  ]?.map(tab => (
                    <button
                      key={tab?.key}
                      onClick={() => setExpandedTab(prev => ({ ...prev, [account?.id]: tab?.key }))}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: currentTab === tab?.key ? `${tab?.color}18` : 'rgba(255,255,255,0.04)',
                        color: currentTab === tab?.key ? tab?.color : 'var(--color-text-secondary)',
                        border: `1px solid ${currentTab === tab?.key ? `${tab?.color}40` : 'rgba(255,255,255,0.08)'}`,
                      }}
                    >
                      <Icon name={tab?.icon} size={12} />
                      {tab?.label}
                      {tab?.count !== null && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: 'rgba(255,255,255,0.1)' }}>
                          {tab?.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Leases Tab */}
                {currentTab === 'leases' && (
                  <div>
                    <h4 className="text-text-primary text-sm font-semibold mb-2 flex items-center gap-2">
                      <Icon name="FileText" size={15} className="text-[#60a5fa]" />
                      Leases ({allLeases?.length})
                    </h4>
                    {allLeases?.length === 0 ? (
                      <p className="text-text-secondary text-xs py-3 text-center">No leases found for this account</p>
                    ) : (
                      <div className="space-y-2">
                        {allLeases?.map(lease => {
                          const ls = LEASE_STATUS_STYLES?.[lease?.lease_status] || LEASE_STATUS_STYLES?.draft;
                          const leaseVehicles = Array.isArray(lease?.vehicles) ? lease?.vehicles : [];
                          return (
                            <div
                              key={lease?.id}
                              className="rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
                              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                            >
                              <div className="flex items-center gap-3 flex-wrap">
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold"
                                  style={{ background: ls?.bg, color: ls?.text }}
                                >
                                  {ls?.label}
                                </span>
                                <span className="text-text-primary text-xs font-mono">{lease?.lease_number || '—'}</span>
                                <span className="text-text-secondary text-xs flex items-center gap-1">
                                  <Icon name="Car" size={11} /> {leaseVehicles?.length} vehicle{leaseVehicles?.length !== 1 ? 's' : ''}
                                </span>
                                <span className="text-text-secondary text-xs flex items-center gap-1">
                                  <Icon name="Calendar" size={11} />
                                  {formatDate(lease?.start_at)} – {formatDate(lease?.end_at)}
                                </span>
                              </div>
                              <span className="text-text-primary text-xs font-semibold">
                                {formatCurrency(lease?.monthly_price)}<span className="text-text-secondary font-normal">/mo</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Vehicle Assignments Tab */}
                {currentTab === 'vehicles' && (
                  <VehicleAssignmentPanel
                    accountId={account?.id}
                    accountName={account?.company_name}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
      {/* Confirm Action Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-surface rounded-2xl border border-border w-full max-w-sm shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: confirmAction?.type === 'suspend' ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.15)' }}
              >
                <Icon
                  name={confirmAction?.type === 'suspend' ? 'PauseCircle' : 'XCircle'}
                  size={20}
                  style={{ color: confirmAction?.type === 'suspend' ? '#fbbf24' : '#ef4444' }}
                />
              </div>
              <div>
                <h3 className="text-text-primary font-bold text-base">
                  {confirmAction?.type === 'suspend' ? 'Suspend Account' : 'Cancel Account'}
                </h3>
                <p className="text-text-secondary text-xs mt-0.5">
                  {confirmAction?.account?.company_name}
                </p>
              </div>
            </div>
            <p className="text-text-secondary text-sm">
              {confirmAction?.type === 'suspend' ?'This will suspend the account. Active leases will remain but no new leases can be created.' :'This will permanently deactivate the account. This action should be used with caution.'}
            </p>
            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                onClick={() => setConfirmAction(null)}
                className="btn-secondary text-sm px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => updateAccountStatus(
                  confirmAction?.account?.id,
                  confirmAction?.type === 'suspend' ? 'suspended' : 'pending'
                )}
                disabled={!!actionLoading}
                className="text-sm px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                style={{
                  background: confirmAction?.type === 'suspend' ? 'rgba(251,191,36,0.2)' : 'rgba(239,68,68,0.2)',
                  color: confirmAction?.type === 'suspend' ? '#fbbf24' : '#ef4444',
                  border: `1px solid ${confirmAction?.type === 'suspend' ? 'rgba(251,191,36,0.4)' : 'rgba(239,68,68,0.4)'}`,
                }}
              >
                {actionLoading ? 'Processing…' : confirmAction?.type === 'suspend' ? 'Suspend' : 'Cancel Account'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {editAccount && (
        <EditAccountModal
          account={editAccount}
          onClose={() => setEditAccount(null)}
          onSaved={() => {
            setEditAccount(null);
            showToast('Account updated successfully');
            fetchAccounts();
          }}
        />
      )}
      {/* Lease Contract Generator */}
      {contractAccount && (
        <LeaseContractGenerator
          accountId={contractAccount?.id}
          accountName={contractAccount?.company_name}
          onClose={() => setContractAccount(null)}
        />
      )}
    </div>
  );
};

export default CorporateAccountsPanel;
